import type { TemplateAttribute } from '@/services/hooks/template_attributes'
import { DEFAULT_STROKE_COLOR } from './constants'
import { DEFAULT_ANNOTATIONS_MENUS } from './menus'
import type {
  KonvaAnnotationKind,
  KonvaAnnotationTemplate,
} from './annotationSchema'

const LEGACY_TYPE_TO_KIND: Record<string, KonvaAnnotationKind> = {
  circle: 'circle',
  diamond: 'diamond',
  ellipse: 'ellipse',
  roundRect: 'roundRect',
  square: 'rect',
  triangle: 'triangle',
}

const COLOR_EDITABLE_ANNOTATION_IDS = new Set(
  DEFAULT_ANNOTATIONS_MENUS.filter((menu) => menu.colorOptionsEnabled).flatMap(
    (menu) => menu.menuItems.map((item) => item.id),
  ),
)

export const isLegacyLineBasedAnnotationType = (
  type?: string | null,
): type is string =>
  typeof type === 'string' &&
  (type === 'line' || type.includes('arrow') || type.includes('bubble'))

export const isFreehandAnnotationType = (type?: string | null) =>
  type?.includes('freehand')

export const FREEHAND_CURSOR =
  "url(\"data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.2 18.8L7 13.6L15.2 5.4L18.6 8.8L10.4 17L5.2 18.8Z' fill='white' stroke='%231E1E1E' stroke-width='1.8' stroke-linejoin='round'/%3E%3Cpath d='M7 13.6L10.4 17' stroke='%231E1E1E' stroke-width='1.8' stroke-linecap='round'/%3E%3Cpath d='M14 6.6L17.4 10' stroke='%231E1E1E' stroke-width='1.8' stroke-linecap='round'/%3E%3C/svg%3E\") 6 18, crosshair"

export const getLineBasedAnnotationKind = (
  type: string,
): Extract<KonvaAnnotationKind, 'line' | 'arrow' | 'bubble'> =>
  type === 'line' ? 'line' : type.includes('bubble') ? 'bubble' : 'arrow'

export const getAnnotationKindForLegacyType = (
  type?: string,
): KonvaAnnotationKind => {
  if (isLegacyLineBasedAnnotationType(type)) {
    return getLineBasedAnnotationKind(type)
  }

  return LEGACY_TYPE_TO_KIND[type ?? ''] ?? 'icon'
}

export const isColorEditableKonvaAnnotation = (
  annotation: KonvaAnnotationTemplate,
) => {
  if (
    annotation.legacyType &&
    COLOR_EDITABLE_ANNOTATION_IDS.has(annotation.legacyType)
  ) {
    return true
  }

  switch (annotation.kind) {
    case 'rect':
    case 'roundRect':
    case 'ellipse':
    case 'circle':
    case 'diamond':
    case 'triangle':
    case 'line':
    case 'arrow':
    case 'bubble':
    case 'freehand':
      return true
    default:
      return false
  }
}

export const getSelectedAnnotationColor = ({
  annotation,
  templateValues,
}: {
  annotation: KonvaAnnotationTemplate
  templateValues: TemplateAttribute<any>['template_values']
}) => {
  if (
    !('schema' in (templateValues ?? {})) &&
    typeof templateValues?.strokeColor === 'string'
  ) {
    return templateValues.strokeColor
  }

  return annotation.style.stroke ?? DEFAULT_STROKE_COLOR
}
